import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import EmailTemplateService from "services/EmailTemplateService";
import Utils from "utils";
import openNotification from "utils/notification";

export const getEmailTemplateApi = createAsyncThunk(
  "emailTemplate/getEmailTemplateApi",
  async (data, { rejectWithValue }) => {
    try {
      const response = await EmailTemplateService.getEmailTemplate(data ?? {});
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const getEmailTemplateDetailApi = createAsyncThunk(
  "emailTemplate/getEmailTemplateDetailApi",
  async (id, { rejectWithValue }) => {
    try {
      const response = await EmailTemplateService.getEmailTemplateDetail(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const addEmailTemplateApi = createAsyncThunk(
  "emailTemplate/addEmailTemplateApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await EmailTemplateService.createEmailTemplate(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const updateEmailTemplateApi = createAsyncThunk(
  "emailTemplate/updateEmailTemplateApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;

      const response = await EmailTemplateService.updateEmailTemplate(payload);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

export const delEmailTemplateApi = createAsyncThunk(
  "emailTemplate/delEmailTemplateApi",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;

      const response = await EmailTemplateService.delEmailTemplate(id);

      if (onSuccess) onSuccess(response);

      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title"),
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  },
);

const initialState = {
  loading: false,
  loadingDetail: false,

  emailTemplateList: [],
  emailTemplateDetail: null,
  currentRequestId: undefined,
};

export const emailTemplateSlice = createSlice({
  name: "emailTemplate",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    setEmailTemplateDetail: (state, action) => {
      state.emailTemplateDetail = action.payload || null;
    },
    clearEmailTemplateDetail: (state) => {
      state.emailTemplateDetail = null;
      state.loadingDetail = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmailTemplateApi.pending, (state, action) => {
        state.loading = true;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getEmailTemplateApi.fulfilled, (state, action) => {
        state.loading = false;
        state.emailTemplateList = action.payload ?? [];
      })
      .addCase(getEmailTemplateApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(getEmailTemplateDetailApi.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(getEmailTemplateDetailApi.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.emailTemplateDetail = action.payload ?? null;
      })
      .addCase(getEmailTemplateDetailApi.rejected, (state) => {
        state.loadingDetail = false;
        state.emailTemplateDetail = null;
      })

      .addCase(addEmailTemplateApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(addEmailTemplateApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addEmailTemplateApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(updateEmailTemplateApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEmailTemplateApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateEmailTemplateApi.rejected, (state) => {
        state.loading = false;
      })

      .addCase(delEmailTemplateApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(delEmailTemplateApi.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(delEmailTemplateApi.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  showLoading,
  setEmailTemplateDetail,
  clearEmailTemplateDetail,
} = emailTemplateSlice.actions;

export default emailTemplateSlice.reducer;